import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, Bell, Heart } from 'lucide-react'
import md5 from 'blueimp-md5'
import { Button } from './button'
import { getNotifications, markNotificationRead } from '../utils/api'
import logo from "../assest/l.png"
import { useAuth } from '../utils/authContext'

export default function MatriHeader() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logoutMatrimony } = useAuth();

    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [userName, setUserName] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const [token, setToken] = useState(null);
    const [gravatarUrl, setGravatarUrl] = useState(null);
    const unreadCount = notifications.filter(n => !n.isRead).length;

    // Load user info and gravatar on route change
    useEffect(() => {
        const name = localStorage.getItem('matrimonyUserName');
        const email = localStorage.getItem('matrimonyUserEmail');
        const token = localStorage.getItem('matrimonyToken');

        setUserName(name);
        setUserEmail(email);
        setToken(token);

        if (email) {
            const hash = md5(email.trim().toLowerCase());
            setGravatarUrl(`https://www.gravatar.com/avatar/${hash}?d=identicon`);
        } else {
            setGravatarUrl(null);
        }
    }, [location]);

    // Scroll effect
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Click outside for dropdowns
    useEffect(() => {
        if (!dropdownOpen && !notifOpen) return;
        const handleClickOutside = (event) => {
            const dd = document.getElementById('matrimony-dropdown');
            const nd = document.getElementById('notification-dropdown');
            if (
                (!dd || !dd.contains(event.target)) &&
                (!nd || !nd.contains(event.target))
            ) {
                setDropdownOpen(false);
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen, notifOpen]);

    // Fetch notifications on mount
    useEffect(() => {
        const userId = localStorage.getItem('matrimonyUserId');
        if (userId) {
            getNotifications(userId)
                .then(data => setNotifications(data.notifications || []))
                .catch(err => console.error('Failed to load notifications', err));
        }
    }, []);

    const handleLogout = () => {
        logoutMatrimony();
    };

    const handleNotificationClick = async (notif) => {
        try {
            await markNotificationRead(notif._id);

            setNotifications((prev) => prev.filter((n) => n._id !== notif._id));

            setNotifOpen(false);

            setTimeout(() => {
                navigate('/dashboard');
            }, 100); 
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }
    };

    return (
        <header
            className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${isScrolled
                ? 'bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-lg shadow-black/5'
                : 'bg-transparent'
                }`}
        >
            <div className="container mx-auto px-6 py-4">
                <div className="flex justify-between items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group">
                        <img src={logo} className="h-10" alt="Logo" />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        <Link
                            to="/matrimony"
                            className={`font-medium transition-colors hover:text-rose-600 ${isScrolled ? 'text-gray-700' : 'text-white'
                                }`}
                        >
                            Home
                        </Link>
                        <Link
                            to="/matrimony-profile"
                            className={`font-medium transition-colors hover:text-rose-600 ${isScrolled ? 'text-gray-700' : 'text-white'
                                }`}
                        >
                            Profiles
                        </Link>
                        <Link
                            to="/matrimony-pricing"
                            className={`font-medium transition-colors hover:text-rose-600 ${isScrolled ? 'text-gray-700' : 'text-white'
                                }`}
                        >
                            Pricing
                        </Link>
                    </nav>

                    {/* Auth & Notifications */}
                    <div className="hidden md:flex items-center gap-4">
                        {userName && token ? (
                            <>
                                {/* Notification Icon */}
                                <div className="relative" id="notification-dropdown">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => setNotifOpen(!notifOpen)}
                                        className={isScrolled ? 'text-gray-700' : 'text-white'}
                                    >
                                        <Bell className="w-5 h-5" />
                                        {unreadCount > 0 && (
                                            <span className="absolute -top-2 -right-2 min-w-[1.25rem] h-5 bg-rose-500 text-white text-xs font-semibold rounded-full flex items-center justify-center">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </Button>

                                    {notifOpen && (
                                        <div className="absolute right-0 mt-2 w-64 bg-white text-black rounded shadow-lg max-h-64 overflow-y-auto z-50 px-4">
                                            {notifications.filter(n => !n.isRead).length === 0 ? (
                                                <p className="p-4 text-sm text-gray-500">No notifications</p>
                                            ) : (
                                                notifications
                                                    .filter((notif) => !notif.isRead)
                                                    .map((notif) => {
                                                        const senderName = notif.senderId?.full_name || "Someone";
                                                        return (
                                                            <div
                                                                key={notif._id}
                                                                onClick={() => handleNotificationClick(notif)}
                                                                className="cursor-pointer hover:bg-gray-100 p-2"
                                                            >
                                                                {notif.message} <strong>{senderName}</strong>
                                                            </div>
                                                        );
                                                    })
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Profile Dropdown */}
                                <div className="relative ml-4" id="matrimony-dropdown">
                                    <button
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                        className="flex items-center gap-2 focus:outline-none"
                                    >
                                        <img
                                            src={gravatarUrl || '/m_header.jpg'}
                                            alt="Profile"
                                            className="rounded-full w-8 h-8 border border-gray-300"
                                        />
                                        <span className={`font-medium ${isScrolled ? 'text-gray-700' : 'text-white'}`}>{userName}</span>
                                        <ChevronDown className={isScrolled ? 'text-gray-700' : 'text-white'} />
                                    </button>
                                    {dropdownOpen && (
                                        <div className="absolute right-0 mt-2 w-40 bg-white text-black rounded-lg shadow-lg py-2 z-50">
                                            <button
                                                onClick={() => {
                                                    setDropdownOpen(false);
                                                    navigate('/dashboard');
                                                }}
                                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                            >
                                                Dashboard
                                            </button>
                                            <button
                                                onClick={handleLogout}
                                                className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/matrimony-login')}
                                >
                                    Login
                                </Button>
                                <Button
                                    onClick={() => navigate('/matrimony-register')}
                                >
                                    Sign Up
                                </Button>
                            </>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </Button>
                    </div>
                </div>
                {/* Mobile Menu */}
                {isOpen && (
                    <div className="md:hidden mt-4 p-4 bg-white/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl">
                        <nav className="flex flex-col gap-4">
                            <Link to="/matrimony" className="font-medium text-gray-700 hover:text-rose-600">
                                Home
                            </Link>
                            <Link to="/matrimony-profile" className="font-medium text-gray-700 hover:text-rose-600">
                                Profiles
                            </Link>
                            <Link to="/matrimony-pricing" className="font-medium text-gray-700 hover:text-rose-600">
                                Pricing
                            </Link>

                            {userName && token ? (
                                <>
                                    <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                                        <img
                                            src={gravatarUrl || '/m_header.jpg'}
                                            alt="Profile"
                                            className="rounded-full w-8 h-8 border border-gray-300"
                                        />
                                        <span className="font-medium text-gray-700">{userName}</span>
                                    </div>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 mt-2 bg-red-50 text-red-600 rounded hover:bg-red-100"
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => navigate('/matrimony-login')}
                                    >
                                        Login
                                    </Button>
                                    <Button
                                        className="w-full mt-2"
                                        onClick={() => navigate('/matrimony-register')}
                                    >
                                        Sign Up
                                    </Button>
                                </>
                            )}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
